import {inject, Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {map, retryWhen, switchMap, tap} from 'rxjs/operators';

import {PAYMENT_STRATEGY_REGISTRY} from '../../domain/ports/strategy/payment-strategy.port';
import {Payment} from '../../domain/entities/payment.entity';
import {PaymentPollingCancelledError, PaymentPollingFailedError} from '../../domain/model/payment.errors';
import {PAYMENT_VERIFICATION_RECOVERY} from '../../domain/ports/ui/recovery.port';
import {PaymentPollingStatus} from '../../domain/model/payment.model';
import {PaymentCommandService} from '../stores/charge/payment-command.service';

@Injectable({providedIn: 'root'})
export class PaymentVerificationService {
  private readonly commands = inject(PaymentCommandService);
  private readonly recovery = inject(PAYMENT_VERIFICATION_RECOVERY);
  private readonly strategyRegistry = inject(PAYMENT_STRATEGY_REGISTRY);

  execute(payment: Payment): Observable<Payment> {
    const strategy = this.strategyRegistry.resolve(payment.gateway);

    return strategy.verify(payment).pipe(
      retryWhen(errors => errors.pipe(
        switchMap(error => this.recovery.recoverVerification(payment, error).pipe(
          switchMap(shouldRetry => shouldRetry ? of(true) : throwError(() => new PaymentPollingCancelledError()))
        ))
      )),
      switchMap(result => result.status === PaymentPollingStatus.FAILED
        ? throwError(() => new PaymentPollingFailedError())
        : of(result)
      ),
      tap(result => result.status === PaymentPollingStatus.COMPLETED && this.commands.markSuccess(payment)),
      tap({error: error => error instanceof PaymentPollingFailedError
        ? this.commands.markFailed(payment, error)
        : this.commands.markError(payment, error)
      }),
      map(() => payment)
    );
  }
}
